import createReducer, { ActionCreatorHelper } from 'store/createReducer';
import { InvoicePayerType } from 'store/models/invoices';

type PayerId = number;
type PayerLegalName = string;
type PayerINN = string;
type PayerKPP = string;
type PayerLegalAddress = string;
type PayerDocAddress = string;
type PayerName = string;
type PayerUserId = string;
type ResetPayer = null;

interface PayerErrors {
    address: string;
    post: string;
    inn: string;
    kpp: string;
    title: string;
    fio: string;
}

// ----------------------- actions -------------------------------------
const SET_CREATE_PAYER_ID = 'SET_CREATE_PAYER_ID';
const SET_CREATE_PAYER_TYPE = 'SET_CREATE_PAYER_TYPE';
const SET_CREATE_PAYER_LEGAL_NAME = 'SET_CREATE_PAYER_LEGAL_NAME';
const SET_CREATE_PAYER_INN = 'SET_CREATE_PAYER_INN';
const SET_CREATE_PAYER_KPP = 'SET_CREATE_PAYER_KPP';
const SET_CREATE_PAYER_LEGAL_ADDRESS = 'SET_CREATE_PAYER_LEGAL_ADDRESS';
const SET_CREATE_PAYER_DOC_ADDRESS = 'SET_CREATE_PAYER_DOC_ADDRESS';
const SET_CREATE_PAYER_NAME = 'SET_CREATE_PAYER_NAME';
const SET_CREATE_PAYER_AVAILABLE_PAYER_TYPES = 'SET_CREATE_PAYER_AVAILABLE_PAYER_TYPES';
const SET_CREATE_PAYER_ERRORS = 'SET_CREATE_PAYER_ERRORS';
const SET_CREATE_PAYER_USER_ID = 'SET_CREATE_PAYER_USER_ID';
const RESET_CREATE_PAYER_INFO = 'RESET_CREATE_PAYER_INFO';

interface PayloadTypes {
    [SET_CREATE_PAYER_ID]: PayerId;
    [SET_CREATE_PAYER_TYPE]: InvoicePayerType;
    [SET_CREATE_PAYER_LEGAL_NAME]: PayerLegalName;
    [SET_CREATE_PAYER_INN]: PayerINN;
    [SET_CREATE_PAYER_KPP]: PayerKPP;
    [SET_CREATE_PAYER_LEGAL_ADDRESS]: PayerLegalAddress;
    [SET_CREATE_PAYER_DOC_ADDRESS]: PayerDocAddress;
    [SET_CREATE_PAYER_NAME]: PayerName;
    [SET_CREATE_PAYER_AVAILABLE_PAYER_TYPES]: InvoicePayerType[];
    [SET_CREATE_PAYER_ERRORS]: Partial<PayerErrors>;
    [SET_CREATE_PAYER_USER_ID]: PayerUserId;
    [RESET_CREATE_PAYER_INFO]: ResetPayer;
}

const ActionCreator = ActionCreatorHelper<PayloadTypes>();

export const setPayerId = ActionCreator(SET_CREATE_PAYER_ID);
export const setPayerType = ActionCreator(SET_CREATE_PAYER_TYPE);
export const setPayerLegalName = ActionCreator(SET_CREATE_PAYER_LEGAL_NAME);
export const setPayerINN = ActionCreator(SET_CREATE_PAYER_INN);
export const setPayerKPP = ActionCreator(SET_CREATE_PAYER_KPP);
export const setPayerLegalAddress = ActionCreator(SET_CREATE_PAYER_LEGAL_ADDRESS);
export const setPayerDocAddress = ActionCreator(SET_CREATE_PAYER_DOC_ADDRESS);
export const setPayerName = ActionCreator(SET_CREATE_PAYER_NAME);
export const setAvailablePayerTypes = ActionCreator(SET_CREATE_PAYER_AVAILABLE_PAYER_TYPES);
export const setPayerErrors = ActionCreator(SET_CREATE_PAYER_ERRORS);
export const setPayerUserId = ActionCreator(SET_CREATE_PAYER_USER_ID);
export const resetCreatePayerInfo = ActionCreator(RESET_CREATE_PAYER_INFO);
// ----------------------- /actions -------------------------------------

// ----------------------- reducer --------------------------------------
interface PayerCreateState {
    payerId: PayerId;
    payerType: InvoicePayerType;
    payerLegalName: PayerLegalName;
    payerINN: PayerINN;
    payerKPP: PayerKPP;
    payerLegalAddress: PayerLegalAddress;
    payerDocAddress: PayerDocAddress;
    payerName: PayerName;
    availablePayerTypes: InvoicePayerType[];
    payerUserId: PayerUserId;
    payerErrors: Partial<PayerErrors>;
}

export const PAYER_CREATE_INITIAL_STATE: PayerCreateState = {
    payerId: 0,
    payerType: InvoicePayerType.Legal,
    payerLegalName: '',
    payerINN: '',
    payerKPP: '',
    payerLegalAddress: '',
    payerDocAddress: '',
    payerName: '',
    availablePayerTypes: [],
    payerUserId: '',
    payerErrors: {},
};

export default createReducer<PayerCreateState, PayloadTypes>(PAYER_CREATE_INITIAL_STATE, {
    [SET_CREATE_PAYER_ID]: (state, { payload }) => ({
        ...state,
        payerId: payload,
    }),
    [SET_CREATE_PAYER_TYPE]: (state, { payload }) => ({
        ...state,
        payerType: payload,
    }),
    [SET_CREATE_PAYER_LEGAL_NAME]: (state, { payload }) => ({
        ...state,
        payerLegalName: payload,
    }),
    [SET_CREATE_PAYER_INN]: (state, { payload }) => ({
        ...state,
        payerINN: payload,
    }),
    [SET_CREATE_PAYER_KPP]: (state, { payload }) => ({
        ...state,
        payerKPP: payload,
    }),
    [SET_CREATE_PAYER_LEGAL_ADDRESS]: (state, { payload }) => ({
        ...state,
        payerLegalAddress: payload,
    }),
    [SET_CREATE_PAYER_DOC_ADDRESS]: (state, { payload }) => ({
        ...state,
        payerDocAddress: payload,
    }),
    [SET_CREATE_PAYER_NAME]: (state, { payload }) => ({
        ...state,
        payerName: payload,
    }),
    [SET_CREATE_PAYER_AVAILABLE_PAYER_TYPES]: (state, { payload }) => ({
        ...state,
        availablePayerTypes: payload,
    }),
    [SET_CREATE_PAYER_ERRORS]: (state, { payload }) => ({
        ...state,
        payerErrors: payload,
    }),
    [SET_CREATE_PAYER_USER_ID]: (state, { payload }) => ({
        ...state,
        payerUserId: payload,
    }),
    [RESET_CREATE_PAYER_INFO]: () => ({
        ...PAYER_CREATE_INITIAL_STATE,
    }),
});
// ----------------------- /reducer --------------------------------------
